import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { PAGE_SIZE, formatDate, formatMoney } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { ConfirmDialog } from '../components/Modal';
import { IconPencil, IconTrash } from '../components/Icons';
import { FilterSubmitButton } from '../components/FilterSubmitButton';

export default function Rewards() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isActive, setIsActive] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { skip, limit };
      if (isActive === 'true' || isActive === 'false') params.isActive = isActive === 'true';
      const raw = await adminApi.listRewards(params);
      const norm = normalizeListResponse(raw, limit);
      setRows(norm.items);
      setTotalCount(norm.totalCount);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [skip, limit, isActive]);

  useEffect(() => {
    load();
  }, [load]);

  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await adminApi.deleteReward(deleteId);
      setDeleteId(null);
      await load();
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  const columns = [
    { key: 'title', label: 'Title', render: (r) => r.title || '—' },
    { key: 'code', label: 'Code', render: (r) => <code>{r.code || '—'}</code> },
    {
      key: 'discount',
      label: 'Discount',
      render: (r) =>
        r.discountType && r.discountValue != null
          ? r.discountType === 'PERCENT'
            ? `${r.discountValue}%`
            : formatMoney(r.discountValue)
          : '—',
    },
    {
      key: 'expiry',
      label: 'Expires',
      render: (r) => formatDate(r.expiryDate),
    },
    {
      key: 'active',
      label: 'Active',
      render: (r) =>
        r.isActive ? <span className="badge text-bg-success">Yes</span> : <span className="badge text-bg-secondary">No</span>,
    },
    {
      key: 'actions',
      label: '',
      width: '108px',
      render: (r) => {
        const id = r.rewardId || r.id || r._id;
        return (
          <div className="table-actions">
            <Link
              to={`/rewards/${id}`}
              className="btn-table-icon btn-table-icon--edit"
              title="Edit"
              aria-label="Edit"
            >
              <IconPencil />
            </Link>
            <button
              type="button"
              className="btn-table-icon btn-table-icon--delete"
              onClick={() => setDeleteId(id)}
              title="Delete"
              aria-label="Delete"
            >
              <IconTrash />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Rewards"
        subtitle="Promo codes and offers"
        actions={
          <Link to="/rewards/new" className="btn btn-primary">
            New reward
          </Link>
        }
      />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label small mb-1">Active</label>
              <select className="form-select" value={isActive} onChange={(e) => setIsActive(e.target.value)}>
                <option value="">Any</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                Filter
              </label>
              <FilterSubmitButton onClick={() => setSkip(0)} />
            </div>
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <DataTable columns={columns} rows={rows} rowKey={(r, i) => r.rewardId || r.id || r._id || i} />
          )}
          <Pagination totalCount={totalCount} skip={skip} limit={limit} onChange={({ skip: s }) => setSkip(s)} />
        </CardBody>
      </Card>

      {deleteId ? (
        <ConfirmDialog
          title="Delete reward"
          message="Remove this promo permanently?"
          confirmLabel="Delete"
          danger
          busy={deleting}
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </>
  );
}
